import React from 'react';
import { useTenants } from '../../contexts/TenantContext';
import { useAuth } from '../../hooks';
import { sitesForTenant } from '../../utils/sites';
import { roleLabel } from '../../utils/userDisplay';

function ChoiceList({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-medium text-gray-500">{label}</h3>
      <div className="overflow-hidden rounded-xl border border-gray-200 divide-y divide-gray-100">{children}</div>
    </section>
  );
}

function ChoiceButton({
  selected,
  disabled,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled || selected}
      onClick={onClick}
      className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm transition-colors disabled:cursor-default ${
        selected ? 'bg-brand-50' : 'hover:bg-gray-50'
      }`}
    >
      <span className={`font-medium ${selected ? 'text-brand-900' : 'text-gray-900'}`}>{children}</span>
      {selected ? <span className="text-xs font-semibold text-brand-700">Current</span> : null}
    </button>
  );
}

export const SessionSwitchPanel: React.FC<{ includeRoles?: boolean }> = ({ includeRoles = true }) => {
  const { accountType, availableRoles, switchRole } = useAuth();
  const {
    switchableTenants,
    currentTenantId,
    currentTenant,
    currentSiteId,
    switchTenant,
    switchSite,
    switching,
    error,
  } = useTenants();

  const organisations = React.useMemo(() => {
    if (currentTenant && !switchableTenants.some(tenant => tenant.id === currentTenant.id)) {
      return [currentTenant, ...switchableTenants];
    }
    return switchableTenants;
  }, [currentTenant, switchableTenants]);

  const sites = sitesForTenant(switchableTenants, currentTenantId);
  const showRoles = includeRoles && availableRoles.length > 1;
  const showOrganisations = organisations.length > 1;
  const showSites = sites.length > 1;

  if (!showRoles && !showOrganisations && !showSites) {
    return null;
  }

  return (
    <div className="space-y-5">
      {showRoles ? (
        <section>
          <h3 className="mb-2 text-xs font-medium text-gray-500">Role</h3>
          <div className="grid gap-1 rounded-xl bg-gray-100 p-1" style={{ gridTemplateColumns: `repeat(${availableRoles.length}, minmax(0, 1fr))` }}>
            {availableRoles.map(role => {
              const selected = role === accountType;
              return (
                <button
                  key={role}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => switchRole(role)}
                  className={`rounded-lg px-2 py-2 text-sm font-semibold transition-colors ${
                    selected
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-brand-800'
                  }`}
                >
                  {roleLabel(role)}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {showOrganisations ? (
        <ChoiceList label="Organisation">
          {organisations.map(tenant => (
            <ChoiceButton
              key={tenant.id}
              selected={tenant.id === currentTenantId}
              disabled={switching}
              onClick={() => void switchTenant(tenant.id)}
            >
              {tenant.name}
            </ChoiceButton>
          ))}
        </ChoiceList>
      ) : null}

      {showSites ? (
        <ChoiceList label="Site">
          {sites.map(site => {
            const siteId = site.tenantSiteId || site.id;
            const selected = site.tenantSiteId === currentSiteId || site.id === currentSiteId;
            return (
              <ChoiceButton
                key={siteId}
                selected={selected}
                disabled={switching}
                onClick={() => void switchSite(siteId)}
              >
                {site.name}
              </ChoiceButton>
            );
          })}
        </ChoiceList>
      ) : null}

      {switching ? <p className="text-xs text-gray-500">Switching…</p> : null}
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
};

export function sessionContextLabel(
  role: string,
  tenantName?: string | null,
  siteName?: string | null,
): string {
  return [role, tenantName, siteName].filter(Boolean).join(' · ');
}
